import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGoals, useCreateGoal, useCompleteGoal } from '../hooks/useGoals';
import { useGeneratePlan, usePlans } from '../hooks/usePlans';
import { useAuthStore } from '../store/authStore';
import { useT } from '../hooks/useT';
import GoalCard from '../components/goals/GoalCard';
import GoalForm from '../components/goals/GoalForm';
import Modal from '../components/common/Modal';
import Button from '../components/common/Button';

const FREE_GOAL_LIMIT = 1;

export default function GoalsPage() {
  const navigate = useNavigate();
  const { isPaid, user } = useAuthStore();
  const t = useT();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { data: goals = [], isLoading } = useGoals();
  const { data: plans = [] } = usePlans();
  // Map goalId → most-recent non-archived plan
  const plansByGoalId = plans.reduce((acc, p) => {
    if (!acc[p.goal_id]) acc[p.goal_id] = p;
    return acc;
  }, {});
  const { mutateAsync: createGoal, isPending: isCreating } = useCreateGoal();
  const { mutateAsync: completeGoal } = useCompleteGoal();
  const [justCompleted, setJustCompleted] = useState(null);
  const { mutateAsync: generatePlan, isPending: isGenerating } = useGeneratePlan();
  const [generatingGoalId, setGeneratingGoalId] = useState(null);
  const [generateError, setGenerateError] = useState(null);

  const activeGoals = goals.filter((g) => g.status === 'active');
  const atLimit = !isPaid() && activeGoals.length >= FREE_GOAL_LIMIT;

  async function handleCreate(data) {
    await createGoal(data);
    setIsModalOpen(false);
  }

  async function handleGeneratePlan(goal) {
    const existing = plansByGoalId[goal.id];
    if (existing) {
      navigate(`/plans/${existing.id}`);
      return;
    }
    setGeneratingGoalId(goal.id);
    setGenerateError(null);
    try {
      const plan = await generatePlan({ goal_id: goal.id });
      // Navigate immediately — PlanPage will poll and show live status
      navigate(`/plans/${plan.id}`);
    } catch (err) {
      const msg = err.response?.data?.error?.message || '';
      if (msg.toLowerCase().includes('profile')) {
        setGenerateError('profile');
      } else {
        setGenerateError(msg || 'Failed to start plan generation. Please try again.');
      }
    } finally {
      setGeneratingGoalId(null);
    }
  }

  async function handleComplete(goal) {
    if (!window.confirm(t('goals.confirm.complete').replace('{title}', goal.title))) return;
    await completeGoal(goal.id);
    setJustCompleted(goal.title);
  }

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-100">{t('goals.title')}</h1>
          <p className="text-slate-400 mt-1">
            {isPaid()
              ? activeGoals.length === 1
                ? t('goals.subtitle.paid').replace('{n}', activeGoals.length)
                : t('goals.subtitle.paid.plural').replace('{n}', activeGoals.length)
              : t('goals.subtitle.free').replace('{n}', activeGoals.length).replace('{limit}', FREE_GOAL_LIMIT)}
          </p>
        </div>
        <Button
          onClick={() => setIsModalOpen(true)}
          disabled={atLimit}
          title={atLimit ? t('goals.upgrade.tooltip') : undefined}
        >
          {t('goals.new')}
        </Button>
      </div>

      {/* Congratulations banner */}
      {justCompleted && (
        <div className="rounded-xl border border-green-700/50 bg-green-950/30 p-4 flex items-center justify-between gap-4">
          <div>
            <p className="text-green-400 font-semibold">{t('goals.congrats.title')}</p>
            <p className="text-green-300/70 text-sm mt-0.5">{t('goals.congrats.message').replace('{title}', justCompleted)}</p>
          </div>
          <button onClick={() => setJustCompleted(null)} className="text-slate-500 hover:text-slate-300 text-xs flex-shrink-0">{t('common.dismiss')}</button>
        </div>
      )}

      {/* Generate plan error banner */}
      {generateError && (
        <div className="rounded-xl border border-red-700/50 bg-red-950/30 p-4 flex items-center justify-between gap-4">
          <p className="text-red-400 text-sm">
            {generateError === 'profile'
              ? t('goals.error.profile')
              : generateError}
          </p>
          {generateError === 'profile' ? (
            <a href="/setup" className="flex-shrink-0 btn-primary text-sm">{t('goals.error.setup_cta')}</a>
          ) : (
            <button onClick={() => setGenerateError(null)} className="text-slate-500 hover:text-slate-300 text-xs">{t('common.dismiss')}</button>
          )}
        </div>
      )}

      {/* Free tier limit banner */}
      {atLimit && (
        <div className="rounded-xl border border-amber-700/50 bg-amber-950/30 p-4 flex items-center justify-between gap-4">
          <div>
            <p className="text-amber-400 font-medium text-sm">{t('goals.limit.title')}</p>
            <p className="text-amber-300/70 text-xs mt-0.5">
              {t('goals.limit.sub')}
            </p>
          </div>
          <a href="/pricing" className="flex-shrink-0 btn-primary text-sm">
            {t('goals.limit.cta')}
          </a>
        </div>
      )}

      {/* Loading state */}
      {isLoading && (
        <div className="grid md:grid-cols-2 gap-4 animate-pulse">
          {[1, 2].map((i) => (
            <div key={i} className="h-52 bg-surface-card rounded-2xl" />
          ))}
        </div>
      )}

      {/* Goals list */}
      {!isLoading && goals.length === 0 && (
        <div className="card text-center py-16">
          <p className="text-5xl mb-4">🎯</p>
          <p className="text-slate-300 font-semibold text-lg">{t('goals.empty.title')}</p>
          <p className="text-slate-500 text-sm mt-2 mb-8">
            {t('goals.empty.sub')}
          </p>
          <Button onClick={() => setIsModalOpen(true)}>{t('goals.empty.cta')}</Button>
        </div>
      )}

      {!isLoading && goals.length > 0 && (
        <div className="space-y-8">
          {activeGoals.length > 0 && (
            <section>
              <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">{t('goals.section.active')}</h2>
              <div className="grid md:grid-cols-2 gap-4">
                {activeGoals.map((goal) => (
                  <GoalCard
                    key={goal.id}
                    goal={goal}
                    activePlan={plansByGoalId[goal.id]}
                    paid={isPaid()}
                    isGenerating={generatingGoalId === goal.id}
                    onGeneratePlan={handleGeneratePlan}
                    onComplete={handleComplete}
                    currency={user?.currency || 'USD'}
                  />
                  ))}
              </div>
            </section>
          )}

          {goals.filter((g) => g.status === 'completed').length > 0 && (
            <section>
              <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">{t('goals.section.completed')}</h2>
              <div className="grid md:grid-cols-2 gap-4 opacity-60">
                {goals
                  .filter((g) => g.status === 'completed')
                  .map((goal) => (
                    <GoalCard
                      key={goal.id}
                      goal={goal}
                      activePlan={plansByGoalId[goal.id]}
                      paid={isPaid()}
                      isGenerating={false}
                      onGeneratePlan={handleGeneratePlan}
                      onComplete={handleComplete}
                      currency={user?.currency || 'USD'}
                    />
                  ))}
              </div>
            </section>
          )}
        </div>
      )}

      {/* Create Goal Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={t('goals.new.modal.title')}
      >
        <GoalForm onSubmit={handleCreate} isSubmitting={isCreating} onCancel={() => setIsModalOpen(false)} />
      </Modal>
    </div>
  );
}
